import React, { useState, useEffect, useCallback } from 'react';
import { FaSortUp, FaSortDown, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import {
    Typography,
    Table,
    TableBody,
    Paper,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    CardContent,
    Card,
    Checkbox,
    Button,
} from '@mui/material';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from 'dayjs/plugin/utc';
import useInvoiceService from "../hooks/useInvoiceService";
import { useSnackbar } from './SnackbarProvider';
import Paginate from "./Paginate"
import { formatDate, itemsPerPage, formatText, handleApiError, IconBtn } from "./Utils"

dayjs.extend(utc);

const InvoiceWithTotalsTable = ({
    title,
    isClient,
    draftsMode = false,
    code,
    status,
    externalProviderId,
    selectedBuilding,
    selectedClient,
    selectedProvider,
    startInvoiceDate,
    endInvoiceDate,
    apply,
    reset
}) => {

    const { getInvoices, getInvoiceDrafts, exportInvoices, exportInvoicesPdf, payInvoicesBulk } = useInvoiceService();
    const showSnackbar = useSnackbar();
    const [error, setError] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('ASC');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [invoices, setInvoices] = useState([]);

    const [totalPriceSum, setTotalPriceSum] = useState(null);
    const [paidAmountSum, setPaidAmountSum] = useState(null);
    const [remainingAmountSum, setRemainingAmountSum] = useState(null);
    const [totalOverdue, setTotalOverdue] = useState(null);

    const fetchInvoices = useCallback(async () => {
        try {
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;
            params.only_client_invoices = isClient;
            params.only_provider_invoices = !isClient;

            if (code) params.code = code;
            if (externalProviderId) params.external_provider_id = externalProviderId;
            if (status) params.invoice_status = status;
            if (selectedBuilding) params.building_id = selectedBuilding.id;
            if (selectedClient) params.client_id = selectedClient.id;
            if (selectedProvider) params.provider_id = selectedProvider.id;
            if (sortColumn) params.sort_by = sortColumn;
            if (sortDirection) params.sort_direction = sortDirection;
            if (startInvoiceDate) {
                const startDateMidday = dayjs(startInvoiceDate).hour(12).minute(0).second(0);
                params.start_invoice_date = startDateMidday.utc().format('YYYY-MM-DD');
            }
            if (endInvoiceDate) {
                const endDateMidday = dayjs(endInvoiceDate).hour(12).minute(0).second(0);
                params.end_invoice_date = endDateMidday.utc().format('YYYY-MM-DD');
            }

            const result = draftsMode ? await getInvoiceDrafts(params) : await getInvoices(params);
            const data = result.data

            setInvoices(data.items);
            setTotalItems(data.total);
            setTotalPriceSum(data.total_price_sum);
            setPaidAmountSum(data.paid_amount_sum);
            setRemainingAmountSum(data.remaining_amount_sum);
            setTotalOverdue(data.total_invoice_overdue);

        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las facturas.'));
        }
    }, [currentPage, externalProviderId, startInvoiceDate, endInvoiceDate, code, status, selectedClient, isClient, draftsMode, selectedBuilding, selectedProvider, sortColumn, sortDirection]);


    useEffect(() => {
        setSelectedIds(new Set());
        fetchInvoices();
    }, [currentPage, sortColumn, sortDirection]);

    useEffect(() => {
        if (apply) {
            setCurrentPage(1);
            fetchInvoices();
        }
    }, [apply]);

    useEffect(() => {
        if (reset) {
            setCurrentPage(1);
            fetchInvoices();
        }
    }, [reset]);

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const buildExportParams = () => {
        const params = {};
        params.only_client_invoices = isClient;
        params.only_provider_invoices = !isClient;
        if (code) params.code = code;
        if (status) params.invoice_status = status;
        if (selectedBuilding) params.building_id = selectedBuilding.id;
        if (selectedClient) params.client_id = selectedClient.id;
        if (selectedProvider) params.provider_id = selectedProvider.id;
        if (startInvoiceDate) {
            const d = dayjs(startInvoiceDate).hour(12).minute(0).second(0);
            params.start_invoice_date = d.utc().format('YYYY-MM-DD');
        }
        if (endInvoiceDate) {
            const d = dayjs(endInvoiceDate).hour(12).minute(0).second(0);
            params.end_invoice_date = d.utc().format('YYYY-MM-DD');
        }
        return params;
    };

    const triggerDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleExportExcel = async () => {
        try {
            const params = buildExportParams();
            const response = await exportInvoices(params);
            const now = new Date();
            const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            triggerDownload(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Facturas_${ts}.xlsx`);
            showSnackbar('Excel generado correctamente.');
        } catch (e) {
            showSnackbar(handleApiError(e, 'Error generando Excel.'), 'error');
        }
    };

    const handleExportPdf = async () => {
        try {
            const params = buildExportParams();
            const response = await exportInvoicesPdf(params);
            const now = new Date();
            const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            triggerDownload(new Blob([response.data], { type: 'application/pdf' }), `Facturas_${ts}.pdf`);
            showSnackbar('PDF generado correctamente.');
        } catch (e) {
            showSnackbar(handleApiError(e, 'Error generando PDF.'), 'error');
        }
    };

    const unpaidInvoices = invoices.filter((inv) => inv.invoice_status !== 'paid');

    const handleToggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(new Set(unpaidInvoices.map((inv) => inv.id)));
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleBulkPay = async () => {
        try {
            await payInvoicesBulk({ invoice_ids: Array.from(selectedIds) });
            showSnackbar(`${selectedIds.size} factura(s) marcadas como pagadas.`);
            setSelectedIds(new Set());
            fetchInvoices();
        } catch (e) {
            showSnackbar(handleApiError(e, 'Error al marcar como pagadas.'), 'error');
        }
    };

    const littleCellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '3%'
    };
    const mediumCellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '7%'
    };

    return (
        <div>
            {/* Title Display */}
            <Grid container spacing={3} justifyContent="space-between" alignItems="center" sx={{ marginBottom: 3 }}>
                <Grid item>
                    <Typography variant="h5" align="left" color="primary">
                        {title}
                    </Typography>
                </Grid>
                {!draftsMode && (
                    <Grid item sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {selectedIds.size > 0 && (
                            <Button variant="contained" color="warning" onClick={handleBulkPay}>
                                Marcar como pagadas ({selectedIds.size})
                            </Button>
                        )}
                        <IconBtn icon={<FaFileExcel />} title="Exportar Excel" variant="outlined" color="success" onClick={handleExportExcel} />
                        <IconBtn icon={<FaFilePdf />} title="Exportar resumen PDF" variant="outlined" color="error" onClick={handleExportPdf} />
                    </Grid>
                )}
            </Grid>
            {/* Sums Display */}
            <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ marginBottom: 3 }}>
                <Grid item xs={12} sm={3}>
                    <Card elevation={3} sx={{ padding: 1.5 }}>
                        <CardContent>
                            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                Total Importe
                            </Typography>
                            <Typography variant="h6" align="center" color="primary">
                                {totalPriceSum ? totalPriceSum.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card elevation={3} sx={{ padding: 1.5 }}>
                        <CardContent>
                            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                Total Pagado
                            </Typography>
                            <Typography variant="h6" align="center" color="primary">
                                {paidAmountSum ? paidAmountSum.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card elevation={3} sx={{ padding: 1.5 }}>
                        <CardContent>
                            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                Total Restante
                            </Typography>
                            <Typography variant="h6" align="center" color="primary">
                                {remainingAmountSum ? remainingAmountSum.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card elevation={3} sx={{ padding: 1.5 }}>
                        <CardContent>
                            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                Total Vencido
                            </Typography>
                            <Typography variant="h6" align="center" color="primary">
                                {totalOverdue ? totalOverdue.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {!draftsMode && (
                                <TableCell sx={littleCellStyle}>
                                    <Checkbox
                                        indeterminate={selectedIds.size > 0 && selectedIds.size < unpaidInvoices.length}
                                        checked={unpaidInvoices.length > 0 && selectedIds.size === unpaidInvoices.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </TableCell>
                            )}
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                        Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            {!isClient && (
                                <TableCell sx={littleCellStyle}>
                                    <Typography variant="h6" gutterBottom>
                                        <span onClick={() => handleSort('external_provider_id')} style={{ cursor: 'pointer' }}>
                                            Código externo {sortColumn === 'external_provider_id' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                        </span>
                                    </Typography>
                                </TableCell>
                            )}
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('invoice_date')} style={{ cursor: 'pointer' }}>
                                        Fecha {sortColumn === 'invoice_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('invoice_limit_date')} style={{ cursor: 'pointer' }}>
                                        Fecha vencimiento {sortColumn === 'invoice_limit_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('building_code')} style={{ cursor: 'pointer' }}>
                                        Obra {sortColumn === 'building_code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    {isClient ? (
                                        <span onClick={() => handleSort('client_code')} style={{ cursor: 'pointer' }}>
                                            Cliente {sortColumn === 'client_code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                        </span>
                                    ) : (
                                        <span onClick={() => handleSort('provider_code')} style={{ cursor: 'pointer' }}>
                                            Proveedor {sortColumn === 'provider_code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                        </span>
                                    )}
                                </Typography>
                            </TableCell>
                            <TableCell sx={littleCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    Estado
                                </Typography>
                            </TableCell>
                            {draftsMode && (
                                <TableCell sx={mediumCellStyle}>
                                    <Typography variant="h6" gutterBottom>
                                        Validaciones
                                    </Typography>
                                </TableCell>
                            )}
                            <TableCell sx={mediumCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('total_price')} style={{ cursor: 'pointer' }}>
                                        Importe {sortColumn === 'total_price' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={mediumCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('total_to_pay_without_retention')} style={{ cursor: 'pointer' }}>
                                        Total a pagar {sortColumn === 'total_to_pay_without_retention' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={mediumCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('retention')} style={{ cursor: 'pointer' }}>
                                        Retención {sortColumn === 'retention' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={mediumCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('paid_amount')} style={{ cursor: 'pointer' }}>
                                        Pagado {sortColumn === 'paid_amount' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                            <TableCell sx={mediumCellStyle}>
                                <Typography variant="h6" gutterBottom>
                                    <span onClick={() => handleSort('remaining_to_pay')} style={{ cursor: 'pointer' }}>
                                        Restante {sortColumn === 'remaining_to_pay' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                    </span>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.map((item) => {
                            const totalToPay = item.total_price - item.retention;
                            const remainingAmount = item.total_price - (item.paid_amount ?? 0) - item.retention;
                            const isPaid = item.invoice_status === 'paid';
                            return (
                                <TableRow key={item.id} selected={selectedIds.has(item.id)}>
                                    {!draftsMode && (
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedIds.has(item.id)}
                                                disabled={isPaid}
                                                onChange={() => handleToggleSelect(item.id)}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell component="th" scope="row">
                                        <Link to={`/invoices/update/${item.id}`}>
                                            {draftsMode ? 'Borrador' : (item.code || '-')}
                                        </Link>
                                    </TableCell>
                                    {!isClient && (
                                        <TableCell component="th" scope="row">
                                            {formatText(item.external_provider_id)}
                                        </TableCell>
                                    )}
                                    <TableCell component="th" scope="row">
                                        {formatDate(item.invoice_date) || '-'}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {formatDate(item.invoice_limit_date) || '-'}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {item.building ? (
                                            <Link to={`/buildings/update/${item.building.id}`}>
                                                {item.building.code || '-'}
                                            </Link>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {isClient ? (
                                            <Link to={`/clients/update/${item.client.id}`}>
                                                {item.client.code || '-'}
                                            </Link>
                                        ) : (
                                            <Link to={`/providers/update/${item.provider.id}`}>
                                                {item.provider.code || '-'}
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {item.invoice_status === 'paid' ? 'PAGADA' :
                                            item.invoice_status === 'not_paid' ? 'NO PAGADA' :
                                                item.invoice_status || '-'}
                                    </TableCell>
                                    {draftsMode && (
                                        <TableCell>
                                            {(item.validations?.length) || 0}/{(item.required_validators?.length) || 0}
                                        </TableCell>
                                    )}
                                    <TableCell component="th" scope="row">
                                        {item.total_price ? (isClient ? item.total_price : -item.total_price).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {totalToPay ? (isClient ? totalToPay : -totalToPay).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {item.retention ? (isClient ? item.retention : -item.retention).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {item.paid_amount ? (isClient ? item.paid_amount : -item.paid_amount).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                    </TableCell>
                                    <TableCell>
                                        {remainingAmount ? (isClient ? remainingAmount : -remainingAmount).toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0,00€'}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <br />
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
        </div>
    )
}
export default InvoiceWithTotalsTable;